import React from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { useGetUnreadCountQuery } from "../../../store/slices/api";
import { SharedHorizontalTabs } from "../../shared";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import Landing from "./Landing";
import Directory from "./Directory";
import MyProfile from "./MyProfile";
import Scrapbook from "../Scrapbook";
import Correspondence from "../Correspondence";

const TABS = [
  { key: "landing", label: "Landing" },
  // Featured (Sam, 2026-09-26): the Scrapbook lives inside Family Connect.
  { key: "scrapbook", label: "Scrapbook" },
  { key: "directory", label: "Directory" },
  { key: "myProfile", label: "My Profile" },
  // Correspondence lives here too (Sam, 2026-09-26). /messages still works.
  { key: "correspondence", label: "Correspondence" },
];

function FamilyConnect() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const { currentTabIndex, setSearchParams } =
    SharedHorizontalTabs.useGetTabIndex(TABS);
  // Same request the sidebar polls, so this is answered from its cache.
  const { data: unreadData } = useGetUnreadCountQuery(individualId, {
    skip: !individualId,
  });
  const unreadCount = unreadData?.unread_count || 0;
  const tabLabel = (tab) =>
    tab.key === "correspondence" && unreadCount > 0
      ? `${tab.label} (${unreadCount})`
      : tab.label;

  const handleTabChange = (index) => {
    setSearchParams({ tab: TABS[index].key }, { replace: true });
  };

  const renderTabContent = () => {
    switch (TABS[currentTabIndex]?.key) {
      case "scrapbook":
        return <Scrapbook embedded />;
      case "directory":
        return <Directory />;
      case "myProfile":
        return <MyProfile />;
      case "correspondence":
        return <Correspondence embedded />;
      case "landing":
      default:
        return <Landing />;
    }
  };

  // Each tab keeps its own block check, so the title and tab row always show:
  // a Family Connect block covers Landing, Directory and My Profile only. The
  // Scrapbook checks its own block, and Correspondence its messaging block.
  const tabKey = TABS[currentTabIndex]?.key;
  const hasOwnBlockCheck = tabKey === "scrapbook" || tabKey === "correspondence";

  return (
    <div className="min-h-screen flex flex-col px-4 md:px-[60px] py-[30px] gap-y-[40px] bg-dupontCream dark:bg-darkPrimary transition-colors duration-200">
      <div className="flex flex-col items-center">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-[42px] font-display font-bold text-dupontBlue dark:text-darkText mb-[12px]">
            Family Connect Hub
          </h1>
          <div className="w-[80px] h-[1px] bg-dupontGold"></div>
        </div>
      </div>
      <div className="flex flex-col gap-y-[30px]">
        <SharedHorizontalTabs.Component
          tabs={TABS.map(tabLabel)}
          initialIndex={currentTabIndex}
          onChange={handleTabChange}
          featuredIndex={TABS.findIndex((t) => t.key === "scrapbook")}
          className="flex-wrap justify-center md:justify-start gap-y-[8px]"
        />
        {hasOwnBlockCheck ? (
          renderTabContent()
        ) : (
          <BlockedFeatureBanner
            featureKey="is_connect_blocked"
            featureName="Family Connect Hub"
            individualId={individualId}
            mode="full"
          >
            {renderTabContent()}
          </BlockedFeatureBanner>
        )}
      </div>
    </div>
  );
}

export default FamilyConnect;
